import Icon, {
  ClearOutlined,
  InfoCircleOutlined,
  ScissorOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import HighlighterIcon from "@images/icons/icon-highlighter.svg?react";
import InterpolateIcon from "@images/icons/icon-interpolate.svg?react";
import MeshIcon from "@images/icons/icon-mesh-organic-boundary.svg?react";
import OverwriteEmptyIcon from "@images/icons/icon-overwrite-empty.svg?react";
import OverwriteEverythingIcon from "@images/icons/icon-overwrite-everything.svg?react";
import RestrictFloodfillToBboxIcon from "@images/icons/icon-restrict-to-bounding-box.svg?react";
import NewSegmentIcon from "@images/icons/icon-segment-new.svg?react";
import { updateNovelUserExperienceInfos } from "admin/rest_api";
import {
  Badge,
  Button,
  Popconfirm,
  Popover,
  Radio,
  type RadioChangeEvent,
  Space,
  theme,
} from "antd";
import FastTooltip from "components/fast_tooltip";
import { usePrevious, useWkSelector } from "libs/react_hooks";
import type React from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  FillModeEnum,
  MappingStatusEnum,
  type OverwriteMode,
  OverwriteModeEnum,
} from "viewer/constants";
import {
  getActiveSegmentationTracing,
  getMappingInfoForVolumeTracing,
  getSegmentColorAsRGBA,
} from "viewer/model/accessors/volumetracing_accessor";
import { clearProofreadingByProducts } from "viewer/model/actions/proofread_actions";
import { updateUserSettingAction } from "viewer/model/actions/settings_actions";
import { showQuickSelectSettingsAction } from "viewer/model/actions/ui_actions";
import { setActiveUserAction } from "viewer/model/actions/user_actions";
import {
  createCellAction,
  interpolateSegmentationLayerAction,
} from "viewer/model/actions/volumetracing_actions";
import { getInterpolationInfo } from "viewer/model/sagas/volume/volume_interpolation_saga";
import { rgbaToCSS } from "viewer/shaders/utils.glsl";
import { Model } from "viewer/singletons";
import type { WebknossosState } from "viewer/store";
import Store from "viewer/store";
import ButtonComponent, { ToggleButton } from "viewer/view/components/button_component";
import { showToastWarningForLargestSegmentIdMissing } from "viewer/view/largest_segment_id_modal";
import { QuickSelectControls } from "../quick_select_settings";
import { ACTIONBAR_MARGIN_LEFT, NARROW_BUTTON_STYLE, RadioButtonWithTooltip } from "./tool_helpers";

function toggleOverwriteMode(overwriteMode: OverwriteMode) {
  if (overwriteMode === OverwriteModeEnum.OVERWRITE_ALL) {
    return OverwriteModeEnum.OVERWRITE_EMPTY;
  } else {
    return OverwriteModeEnum.OVERWRITE_ALL;
  }
}

const handleToggleAutomaticMeshRendering = (value: boolean) => {
  Store.dispatch(updateUserSettingAction("autoRenderMeshInProofreading", value));
};

const handleToggleSelectiveVisibilityInProofreading = (value: boolean) => {
  Store.dispatch(updateUserSettingAction("selectiveVisibilityInProofreading", value));
};

const handleToggleIsMultiSplitActive = (value: boolean) => {
  Store.dispatch(updateUserSettingAction("isMultiSplitActive", value));
};

const handleCreateCell = () => {
  const volumeTracing = getActiveSegmentationTracing(Store.getState());

  if (volumeTracing == null || volumeTracing.tracingId == null) {
    return;
  }

  if (volumeTracing.largestSegmentId != null) {
    Store.dispatch(createCellAction(volumeTracing.activeCellId, volumeTracing.largestSegmentId));
  } else {
    showToastWarningForLargestSegmentIdMissing(volumeTracing);
  }
};

const handleToggleOverwriteMode = () => {
  const { overwriteMode } = Store.getState().userConfiguration;
  Store.dispatch(updateUserSettingAction("overwriteMode", toggleOverwriteMode(overwriteMode)));
};

export function OverwriteModeSwitch({
  isControlOrMetaPressed,
  isShiftPressed,
  visible,
}: {
  isControlOrMetaPressed: boolean;
  isShiftPressed: boolean;
  visible: boolean;
}) {
  // Only CTRL should modify the overwrite mode. CTRL + Shift can be used to switch to the
  // erase tool, which should not affect the default overwrite mode.
  const overwriteMode = useWkSelector((state) => state.userConfiguration.overwriteMode);
  const [previousIsControlOrMetaPressed] = usePrevious(isControlOrMetaPressed);
  const [previousIsShiftPressed] = usePrevious(isShiftPressed);
  // biome-ignore lint/correctness/useExhaustiveDependencies: overwriteMode does not need to be a dependency.
  useEffect(() => {
    // There are four possible states:
    // (1) no modifier is pressed
    // (2) CTRL is pressed
    // (3) Shift is pressed
    // (4) CTRL + Shift is pressed
    // The overwrite mode needs to be toggled when
    // - switching from state (1) to (2) (or vice versa)
    // - switching from state (2) to (4) (or vice versa)
    // Consequently, the mode is only toggled effectively, when CTRL is pressed.
    // Alternatively, we could store the selected value and the overridden value
    // separately in the store. However, this solution works, too.
    const needsModeToggle =
      (!isShiftPressed &&
        isControlOrMetaPressed &&
        previousIsControlOrMetaPressed === previousIsShiftPressed) ||
      (isShiftPressed === isControlOrMetaPressed &&
        !previousIsShiftPressed &&
        previousIsControlOrMetaPressed);

    if (needsModeToggle) {
      Store.dispatch(updateUserSettingAction("overwriteMode", toggleOverwriteMode(overwriteMode)));
    }
  }, [
    isControlOrMetaPressed,
    isShiftPressed,
    previousIsControlOrMetaPressed,
    previousIsShiftPressed,
  ]);

  if (!visible) {
    // This component's hooks should still be active, even when the component is invisible.
    // Otherwise, the toggling of the overwrite mode via "Ctrl" wouldn't work consistently
    // when being combined with other modifiers, which hide the component.
    return null;
  }

  const isOverwriteEmpty = overwriteMode === OverwriteModeEnum.OVERWRITE_EMPTY;
  // Wrap the button in Space.Compact so that the enlarged action bar icon size applies to it.
  return (
    <Space.Compact style={{ marginLeft: ACTIONBAR_MARGIN_LEFT }}>
      <ToggleButton
        active={isOverwriteEmpty}
        onClick={handleToggleOverwriteMode}
        style={NARROW_BUTTON_STYLE}
        title={
          isOverwriteEmpty
            ? "Overwrite-empty mode is active: brushing and erasing will only affect empty voxels (or, when erasing, voxels of the current segment ID). Click to switch to overwriting everything instead. This setting can be toggled by holding CTRL."
            : "Overwrite-everything mode is active: brushing and erasing will overwrite any voxel, regardless of whether it is empty or already labeled. Click to switch to only overwriting empty areas. This setting can be toggled by holding CTRL."
        }
        icon={
          isOverwriteEmpty ? (
            <Icon component={OverwriteEmptyIcon} aria-label="Overwrite Empty Icon" />
          ) : (
            <Icon component={OverwriteEverythingIcon} aria-label="Overwrite All Icon" />
          )
        }
      />
    </Space.Compact>
  );
}

export function VolumeInterpolationButton() {
  const dispatch = useDispatch();

  const onInterpolateClick = (e: React.MouseEvent<HTMLElement> | null) => {
    e?.currentTarget.blur();
    dispatch(interpolateSegmentationLayerAction());
  };

  const { tooltipTitle, isDisabled } = useWkSelector((state) =>
    getInterpolationInfo(state, "Not available since"),
  );

  return (
    <Space.Compact>
      <FastTooltip title={tooltipTitle}>
        <Button
          icon={<Icon component={InterpolateIcon} />}
          onClick={onInterpolateClick}
          disabled={isDisabled}
          style={{ padding: "0 5px 0 6px" }}
        />
      </FastTooltip>
    </Space.Compact>
  );
}

const mapId = (volumeTracingId: string | null | undefined, id: bigint) => {
  // Note that the return value can be an unmapped id even when
  // a mapping is active, if it is a HDF5 mapping that is partially loaded
  // and no entry exists yet for the input id.
  if (!volumeTracingId) {
    return null;
  }
  const { cube } = Model.getSegmentationTracingLayer(volumeTracingId);

  return cube.mapId(id);
};

const getUnmappedActiveCellId = (state: WebknossosState) =>
  getActiveSegmentationTracing(state)?.activeCellId || 0n;

// The cube reads the mapping from the store. Mapping the id in a selector keeps it
// up to date when the mapping changes (e.g., when HDF5 entries are loaded).
const getMappedActiveCellId = (state: WebknossosState) => {
  const volumeTracingId = getActiveSegmentationTracing(state)?.tracingId;
  const unmappedActiveCellId = getUnmappedActiveCellId(state);
  const { mappingStatus } = getMappingInfoForVolumeTracing(state, volumeTracingId);
  return mappingStatus === MappingStatusEnum.ENABLED
    ? mapId(volumeTracingId, unmappedActiveCellId)
    : unmappedActiveCellId;
};

export function CreateSegmentButton() {
  const volumeTracingId = useWkSelector((state) => getActiveSegmentationTracing(state)?.tracingId);
  const { mappingStatus } = useWkSelector((state) =>
    getMappingInfoForVolumeTracing(state, volumeTracingId),
  );
  const unmappedActiveCellId = useWkSelector(getUnmappedActiveCellId);
  const isMappingEnabled = mappingStatus === MappingStatusEnum.ENABLED;
  const activeCellId = useWkSelector(getMappedActiveCellId);

  const activeCellColor = useWkSelector((state) => {
    if (!activeCellId) {
      return null;
    }
    return rgbaToCSS(getSegmentColorAsRGBA(state, activeCellId));
  });

  const mappedIdInfo = isMappingEnabled ? ` (currently mapped to ${activeCellId})` : "";
  return (
    <Badge
      dot
      style={{
        boxShadow: "none",
        background: activeCellColor || "transparent",
        zIndex: 1000,
      }}
    >
      <ButtonComponent
        onClick={handleCreateCell}
        style={{
          ...NARROW_BUTTON_STYLE,
        }}
        title={`Create a new segment id (C) – The active segment id is ${unmappedActiveCellId}${mappedIdInfo}.`}
        icon={<Icon component={NewSegmentIcon} aria-label="New Segment Icon" />}
      />
    </Badge>
  );
}

function IdentityComponent({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function NuxPopConfirm({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const activeUser = useWkSelector((state) => state.activeUser);
  const { token } = theme.useToken();

  return (
    <Popconfirm
      open
      title="Did you know?"
      showCancel={false}
      onConfirm={() => {
        if (!activeUser) {
          return;
        }
        const [newUserSync] = updateNovelUserExperienceInfos(activeUser, {
          hasSeenSegmentAnythingWithDepth: true,
        });
        dispatch(setActiveUserAction(newUserSync));
      }}
      description="The AI-based Quick Select can now be triggered with a single click. Also, it can be run for multiple sections at once (open the settings here to enable this)."
      styles={{ root: { maxWidth: 400 } }}
      icon={<InfoCircleOutlined style={{ color: token.colorInfo }} />}
    >
      {children}
    </Popconfirm>
  );
}

export function QuickSelectSettingsPopover() {
  const dispatch = useDispatch();
  const { quickSelectState, areQuickSelectSettingsOpen } = useWkSelector(
    (state) => state.uiInformation,
  );
  const isQuickSelectActive = quickSelectState === "active";
  const activeUser = useWkSelector((state) => state.activeUser);

  const showNux =
    activeUser != null && !activeUser.novelUserExperienceInfos.hasSeenSegmentAnythingWithDepth;
  const Wrapper = showNux ? NuxPopConfirm : IdentityComponent;

  return (
    <Wrapper>
      <Popover
        trigger="click"
        placement="bottom"
        open={areQuickSelectSettingsOpen}
        content={<QuickSelectControls />}
        onOpenChange={(open: boolean) => {
          dispatch(showQuickSelectSettingsAction(open));
        }}
      >
        <ToggleButton
          title="Configure Quick Select"
          tooltipPlacement="right"
          active={isQuickSelectActive || showNux}
          style={{ ...NARROW_BUTTON_STYLE, marginLeft: ACTIONBAR_MARGIN_LEFT }}
          icon={<SettingOutlined />}
        />
      </Popover>
    </Wrapper>
  );
}

const handleSetFillMode = (event: RadioChangeEvent) => {
  Store.dispatch(updateUserSettingAction("fillMode", event.target.value));
};

export function FloodFillSettings() {
  const dispatch = useDispatch();
  const isRestrictedToBoundingBox = useWkSelector(
    (state) => state.userConfiguration.isFloodfillRestrictedToBoundingBox,
  );
  const toggleRestrictFloodfillToBoundingBox = () => {
    dispatch(
      updateUserSettingAction("isFloodfillRestrictedToBoundingBox", !isRestrictedToBoundingBox),
    );
  };
  return (
    <div>
      <FillModeSwitch />

      {/* Wrap the button in Space.Compact so that the enlarged action bar icon size applies to it. */}
      <Space.Compact style={{ marginLeft: ACTIONBAR_MARGIN_LEFT }}>
        <ButtonComponent
          style={NARROW_BUTTON_STYLE}
          type={isRestrictedToBoundingBox ? "primary" : "default"}
          onClick={toggleRestrictFloodfillToBoundingBox}
          title={
            "When enabled, the floodfill will be restricted to the bounding box enclosed by the clicked position. If multiple bounding boxes enclose that position, the smallest is used."
          }
          icon={<Icon component={RestrictFloodfillToBboxIcon} aria-label="Restrict floodfill" />}
        />
      </Space.Compact>
    </div>
  );
}

function FillModeSwitch() {
  const fillMode = useWkSelector((state) => state.userConfiguration.fillMode);
  return (
    <Radio.Group value={fillMode} onChange={handleSetFillMode}>
      <RadioButtonWithTooltip
        title="Only perform the Fill operation in the current plane."
        style={NARROW_BUTTON_STYLE}
        value={FillModeEnum._2D}
      >
        2D
      </RadioButtonWithTooltip>
      <RadioButtonWithTooltip
        title="Perform the Fill operation in 3D."
        style={NARROW_BUTTON_STYLE}
        value={FillModeEnum._3D}
      >
        3D
      </RadioButtonWithTooltip>
    </Radio.Group>
  );
}

export function ProofreadingComponents() {
  const dispatch = useDispatch();
  const handleClearProofreading = () => dispatch(clearProofreadingByProducts());
  const autoRenderMeshes = useWkSelector(
    (state) => state.userConfiguration.autoRenderMeshInProofreading,
  );
  const selectiveVisibilityInProofreading = useWkSelector(
    (state) => state.userConfiguration.selectiveVisibilityInProofreading,
  );

  const isMultiSplitActive = useWkSelector((state) => state.userConfiguration.isMultiSplitActive);

  return (
    <Space.Compact>
      <ButtonComponent
        title="Clear auxiliary meshes that were loaded while proofreading segments. Use this if you are done with correcting mergers or splits in a segment pair."
        onClick={handleClearProofreading}
        style={NARROW_BUTTON_STYLE}
        icon={<ClearOutlined />}
      />
      <ToggleButton
        title={`${autoRenderMeshes ? "Disable" : "Enable"} automatic loading of meshes`}
        active={autoRenderMeshes}
        style={NARROW_BUTTON_STYLE}
        onClick={() => handleToggleAutomaticMeshRendering(!autoRenderMeshes)}
        icon={<Icon component={MeshIcon} />}
      />
      <ToggleButton
        active={selectiveVisibilityInProofreading}
        title={`${
          selectiveVisibilityInProofreading ? "Disable" : "Enable"
        } selective segment visibility. When enabled, only hovered or active segments will be shown.`}
        style={NARROW_BUTTON_STYLE}
        onClick={() =>
          handleToggleSelectiveVisibilityInProofreading(!selectiveVisibilityInProofreading)
        }
        icon={<Icon component={HighlighterIcon} />}
      />
      <ToggleButton
        active={isMultiSplitActive}
        title={`${
          isMultiSplitActive ? "Disable" : "Enable"
        } multi splitting. When enabled, two partitions can be selected in the orthogonal or 3D viewports to split more accurately.`}
        style={NARROW_BUTTON_STYLE}
        onClick={() => handleToggleIsMultiSplitActive(!isMultiSplitActive)}
        icon={<ScissorOutlined />}
      />
    </Space.Compact>
  );
}
